import dayjs, { type Dayjs } from "dayjs"

import { ContractStatus } from "@/types"
import {
  type Custodian,
  type Child,
  type CreateChildInput,
  type CustodianChild,
  type Contract,
} from "@/graphql/types"
import { type StepKey } from "@/store/child"
import { isInDateRange } from "@/utils/date"
import { type Schema as CustodianSchema } from "@/views/children/children/forms/FormLocalGuardian.vue"

import { removeTypename } from "./data"

const ALLOWED_STATES = new Set<ContractStatus>([
  ContractStatus.Open,
  ContractStatus.Queued,
  ContractStatus.Reserved,
])

export function transformChildToCreateInput(child: Child): CreateChildInput {
  // Transform the child object to CreateChildInput format
  return {
    active: child.active,
    address: child.address ? removeTypename(child.address) : undefined,
    allergies: child.allergies,
    allowedToGoHomeAlone: child.allowedToGoHomeAlone,
    birth: child.birth ? removeTypename(child.birth) : undefined,
    childId: child.childId,
    collectorNotes: child.collectorNotes,
    collectors: child.collectors?.map((collector) => removeTypename(collector)),
    contact: child.contact ? removeTypename(child.contact) : undefined,
    disability: child.disability ? removeTypename(child.disability) : undefined,
    emergencyMeasures: child.emergencyMeasures,
    externalSiblings: child.externalSiblings?.map((sibling) => removeTypename(sibling)),
    healthInsurance: child.healthInsurance,
    person: removeTypename(child.person),
    pictureId: child.pictureId,
    previousInstitutions: child.previousInstitutions,
    remarks: child.remarks,
    symbol: child.symbol,
    vaccination: child.vaccination ? removeTypename(child.vaccination) : undefined,
    visitingNotes: child.visitingNotes,
  }
}

export const mapCustodianChildToCustodianSchema = (
  childCustodian: CustodianChild | CustodianSchema
): CustodianSchema => ({
  allowedToCare: childCustodian.allowedToCare,
  alone: childCustodian.alone,
  aloneCare: childCustodian.aloneCare,
  post: childCustodian.post,
  remarks: childCustodian.remarks,
  relation: childCustodian.relation,
  custodian: {
    profession: childCustodian.custodian?.profession,
    maritalStatus: childCustodian.custodian?.maritalStatus,
    honoraryPost: childCustodian.custodian?.honoraryPost,
    financialAccountingData: {
      fadNumber: childCustodian.custodian?.financialAccountingData?.fadNumber ?? "",
      objectNumber: childCustodian.custodian?.financialAccountingData?.objectNumber ?? "",
    },
    employerId: childCustodian.custodian?.employerId,
    collectiveInvoice: childCustodian.custodian?.collectiveInvoice ?? false,
    person: {
      firstname: childCustodian.custodian?.person?.firstname ?? "",
      lastname: childCustodian.custodian?.person?.lastname ?? "",
      gender: childCustodian.custodian?.person?.gender ?? "",
      citizenships: [...(childCustodian.custodian?.person?.citizenships ?? [])],
      denomination: childCustodian.custodian?.person?.denomination,
      salutation: childCustodian.custodian?.person?.salutation,
      title: childCustodian.custodian?.person?.title,
    },
    address: {
      city: childCustodian.custodian?.address?.city,
      country: childCustodian.custodian?.address?.country,
      district: childCustodian.custodian?.address?.district,
      street: childCustodian.custodian?.address?.street,
      zipCode: childCustodian.custodian?.address?.zipCode,
    },
    birth: {
      date: childCustodian.custodian?.birth?.date,
      country: childCustodian.custodian?.birth?.country,
    },
    contact: {
      email: childCustodian.custodian?.contact?.email,
      email2: childCustodian.custodian?.contact?.email2,
      phone: childCustodian.custodian?.contact?.phone,
      phoneMobile: childCustodian.custodian?.contact?.phoneMobile,
      phoneWork: childCustodian.custodian?.contact?.phoneWork,
      letterSalutation: childCustodian.custodian?.contact?.letterSalutation,
    },
  },
})

export const mapCustodianChildren = (custodian?: Custodian | null) =>
  custodian?.custodies.map((childCustodian) => ({
    id: childCustodian.id,
    child: {
      id: childCustodian.child?.id,
      person: {
        firstname: childCustodian.child?.person?.firstname,
        lastname: childCustodian.child?.person?.lastname,
      },
      birth: {
        date: childCustodian.child?.birth?.date,
      },
    },
  })) ?? []

export function saveEncryptedChildId(id: string) {
  const encryptedId = btoa(unescape(id))
  localStorage.setItem("encryptedChildId", encryptedId)
}

export function getDecryptedChildId() {
  const encryptedId = localStorage.getItem("encryptedChildId")
  if (!encryptedId) return null

  try {
    return escape(atob(encryptedId))
  } catch (e) {
    console.error("Failed to decrypt ID:", e)
    return null
  }
}

export const clearChildId = function () {
  localStorage.removeItem("encryptedChildId")
}

export function saveCompletionState(state: Record<StepKey, boolean>) {
  try {
    localStorage.setItem("child-create:completionState", JSON.stringify(state))
  } catch (err) {
    console.error("Error saving completionState:", err)
  }
}

export function loadCompletionState(): Record<StepKey, boolean> | null {
  try {
    const raw = localStorage.getItem("child-create:completionState")
    return raw ? JSON.parse(raw) : null
  } catch (err) {
    console.error("Error loading completionState:", err)
    return null
  }
}

export function clearCompletionState() {
  try {
    localStorage.removeItem("child-create:completionState")
  } catch (err) {
    console.error("Error clearing completionState:", err)
  }
}

export function nameSuffix(
  firstname: string | undefined,
  lastname: string | undefined,
  divider: string
) {
  const first = firstname?.trim() ?? ""
  const last = lastname?.trim() ?? ""

  if (!first && !last) return ""

  return `${divider} ${first} ${last}`
}

export function getLatestContract(child: Child): Contract | undefined {
  const contracts = child.contracts ?? []
  if (!contracts.length) return undefined

  return contracts.reduce((prev, next) =>
    new Date(next.startOfContract) > new Date(prev.startOfContract) ? next : prev
  )
}

export function isActiveContract(contract: Contract, selectedDate: dayjs.Dayjs): boolean {
  const state = contract.state?.__typename?.toLowerCase() as ContractStatus
  const allowedStates: ContractStatus[] = [ContractStatus.Contracted, ContractStatus.Terminated]
  if (!state || !allowedStates.includes(state)) return false
  return isInDateRange(selectedDate, contract.startOfContract, contract.endOfContract)
}

export function isContractRegisteredAt(contract: Contract, selectedDate: Dayjs): boolean {
  const startDate = dayjs(contract.startOfContract)
  const endDate = contract.endOfContract ? dayjs(contract.endOfContract) : null

  const typename = contract.state?.__typename?.toLowerCase() ?? ""
  const isAllowedState = ALLOWED_STATES.has(typename as ContractStatus)
  if (!isAllowedState) return false

  const afterStart = selectedDate.isSameOrAfter(startDate)
  if (!afterStart) return false

  return endDate ? selectedDate.isBefore(endDate) : true
}

export function hasRegisteredContractAt(contracts: Array<Contract>, selectedDate: Dayjs): boolean {
  return contracts.filter(Boolean).some((c) => isContractRegisteredAt(c as Contract, selectedDate))
}

export function isInactiveByReferenceDate(contracts: Contract[], selectedDate: Dayjs): boolean {
  return contracts.some((contract) => {
    const state = contract.state?.__typename?.toLowerCase()

    const allowStates = state
      ? [ContractStatus.Contracted, ContractStatus.Terminated].includes(state as ContractStatus)
      : false

    if (!allowStates) return false

    const endDate = contract.endOfContract ? dayjs(contract.endOfContract) : null
    return !!endDate && endDate.isSameOrBefore(selectedDate)
  })
}

export function* range(min: number, max: number) {
  for (let i = min; i <= max; i++) yield i
}

export function getSmallestFreeId(usedIds: Set<number>, min: number, max: number): number {
  for (const id of range(min, max)) {
    if (!usedIds.has(id)) return id
  }
  return max
}
